/**
 * THE AUDIT EVENT CATALOGUE AND THE SAFE-VALUE CONTRACT — plan-014 C4a (design §27.3.6, §27.10.12;
 * FR12-SEC-08).
 *
 * > Every new access, lifecycle, security and financial action writes an `audit_entry` (customer-visible)
 * > and, for operator actions, an `operator_action`. Each entry has the real actor, the effective actor,
 * > the target scope, the action, the time, the result and safe before/after fields. Secrets and unmasked
 * > conversation content are never logged. … Finance members see financial events only.
 *
 * This module is the pure half of that sentence; `packages/db/src/tenant/audit-entry-writer.ts` is the
 * half that touches the database. Four things live here:
 *
 *   1. {@link AUDIT_ACTION_CATALOGUE} — the CLOSED list of every action plan-014 audits, each with its
 *      category (access / lifecycle / security / financial / operator), whether it is a mutation or a
 *      sensitive read, the target scopes it may name, and its finance visibility. `visible_to_finance`
 *      is never chosen by a caller: the writer copies it from here, and the catalogue's own test holds
 *      "visible to finance ⇔ financial" for every row, which is what "Finance members see financial
 *      events only" means as data.
 *   2. {@link AuditActor} — who acted, as the real actor and the effective actor: a person as
 *      themselves (optionally through a dedicated-balance delegation, DD14-15), an API key standing for
 *      its owner (or for nobody: an account integration), an operator acting as themselves or through
 *      an impersonation grant on a subject person, or a background job.
 *   3. {@link AuditEvent} — what happened: the action, the target scope, the result and the safe
 *      before/after values.
 *   4. The safe-value contract: {@link auditValue} is the ONLY way to make the before/after value an
 *      event carries ({@link AuditValue} is branded), and its parameter type makes a secret-shaped key a
 *      COMPILE error ({@link NoSecretShapedKey}). It re-checks at run time ({@link auditValueRefusal}),
 *      and migration 0081's `audit_value_is_safe` CHECK refuses the same keys at the database — three
 *      layers, one rule ({@link isRefusedAuditKey}), which the writer's pg suite proves agree.
 *
 * ── THE KEY RULE ──────────────────────────────────────────────────────────────────────────────────
 * A key is normalised (camelCase → snake_case, `-`, `.` and spaces → `_`, lower-cased) and split on `_`.
 * It is refused when ANY segment is a secret or content word ({@link REFUSED_AUDIT_KEY_SEGMENT}:
 * `password`, `token`, `seed`, `iban`, `pan`, `transcript`, …) or the WHOLE key is one of
 * {@link REFUSED_AUDIT_KEY} (`code`, `recovery_code`, `party_address`, … — words that are only dangerous
 * on their own, so `country_code` and `action_code` stay legal). A key ending `_last_four` or `_masked`
 * is allowed whatever precedes it: it names a value that is masked by construction. The rule applies at
 * every depth, inside arrays too. design §27.3.6's CHECK names seven top-level keys; this is a superset
 * of it at every depth.
 *
 * ── WHY THE CODES ARE STRING VALUES, NOT OBJECT KEYS ──────────────────────────────────────────────
 * The same reason `compliance/audit-action.ts` gives: a code is wire vocabulary, and the glossary lint
 * reads declarations, not string contents. The codes are dotted, `<prefix>.<what happened>`, as every
 * writer in the platform spells them; where a pre-plan-014 code already says exactly this (`account.created`,
 * `membership.invited`, `api_key.revoked`, `audit_entry.exported`, …) it is reused rather than respelled,
 * so the customer's log does not grow two names for one act.
 *
 * NOT HERE, deliberately: cost prices (CP) and status publishing (ST-A). They concern no account, so they
 * are written to `staff.operator_action` only and never reach a customer's log.
 */
export const AUDIT_CATEGORY = Object.freeze([
    "access",
    "lifecycle",
    "security",
    "financial",
    "operator",
]);
export const AUDIT_TARGET_SCOPE_KIND = Object.freeze([
    "account",
    "workspace",
    "billing_account",
]);
export const AUDIT_RESULT = Object.freeze(["succeeded", "refused", "failed"]);
const A = ["account"];
const W = ["workspace"];
const B = ["billing_account"];
const AW = ["account", "workspace"];
const AB = ["account", "billing_account"];
const WB = ["workspace", "billing_account"];
function access(code, scope) {
    return { code, category: "access", kind: "mutation", scope, visibleToFinance: false };
}
function lifecycle(code, scope) {
    return { code, category: "lifecycle", kind: "mutation", scope, visibleToFinance: false };
}
function security(code, scope) {
    return { code, category: "security", kind: "mutation", scope, visibleToFinance: false };
}
function financial(code, scope) {
    return { code, category: "financial", kind: "mutation", scope, visibleToFinance: true };
}
function operator(code, scope) {
    return { code, category: "operator", kind: "mutation", scope, visibleToFinance: false };
}
function sensitiveRead(code, category, scope) {
    return { code, category, kind: "sensitive_read", scope, visibleToFinance: category === "financial" };
}
/**
 * The closed catalogue. Grouped by the plan-014 unit whose routes write each action (the unit names
 * are where a reader looks for the writer; they are not stored).
 */
const CATALOGUE = [
    // ── Access: who may enter what (A2/B1/B2/B3/B5; FR12-AUTH-07, FR12-ONB, FR12-SHR) ─────────────────
    access("account_membership.added", A), // B2: an existing person given an account role
    access("account_membership.role_changed", A),
    access("account_membership.removed", A),
    access("account_invitation.sent", A),
    access("account_invitation.resent", A),
    access("account_invitation.revoked", A),
    access("account_invitation.accepted", A),
    access("account_invitation.expired", A), // the invitation-expiry worker, a system actor
    access("account.ownership_transfer_started", A), // F3b: awaiting the owner's confirmation
    access("account.ownership_transferred", A),
    access("account.workspace_access_granted", W), // FR12-AUTH-07: explicit, with a reason
    access("membership.invited", W), // pre-plan-014 code, now with the six workspace roles
    access("membership.accepted", W),
    access("membership.invitation_revoked", W),
    access("membership.role_changed", W),
    access("membership.removed", W),
    access("membership.domain_joined", A), // INT (owner answer 47, 2026-10-06): a verified domain's offer accepted
    access("workspace_access_policy.changed", W), // B3
    access("resource_share.published", W), // B5
    access("resource_share.accepted", W),
    access("resource_share.revoked", W),
    // ── Lifecycle: an account or a workspace coming, changing and going (B1/B2/B3/B4) ─────────────────
    lifecycle("account.created", A),
    lifecycle("account.profile_changed", A),
    lifecycle("account.workspace_creation_policy_changed", A),
    lifecycle("account.closure_started", A),
    lifecycle("account.closure_blocked", A), // preflight blockers stored (§27.4)
    lifecycle("account.closure_withdrawn", A),
    lifecycle("account.closure_undone", A),
    lifecycle("account.closure_access_revoked", A),
    lifecycle("account.closure_content_purged", A),
    lifecycle("account.closure_numbers_released", A), // INT (owner answer 58): WeTalk-held numbers back to the carrier
    lifecycle("account.closed", A),
    lifecycle("workspace.created", W),
    lifecycle("workspace.renamed", W),
    lifecycle("workspace.time_zone_changed", W),
    lifecycle("workspace.archive_started", W), // draining
    lifecycle("workspace.archived", W),
    lifecycle("workspace.restored", W),
    lifecycle("workspace.access_policy_changed", W), // B3: a workspace's join rules (0089)
    // ── Security: how people prove who they are, and what the organization requires (C1/C2/C3) ────────
    security("person_security.mfa_enrolled", A), // C1, written into each account the person belongs to
    security("person_security.mfa_removed", A),
    security("person_security.recovery_codes_regenerated", A),
    security("person_security.recovery_code_redeemed", A),
    security("person_security.sign_in_method_linked", A),
    security("person_security.sign_in_method_unlinked", A),
    security("person_security.password_changed", A),
    security("person_security.email_changed", A),
    security("person_security.devices_signed_out", A),
    security("person_security.new_device_signed_in", A), // plan-014 GAP: a sign-in from a device not used before (C4b notifies)
    security("person_security.password_reset", A), // 2026-10-12: a password set from an emailed reset link; every device signed out (the api tells the person itself)
    security("person_security.help_page_reading_changed", A), // ADR 0072 (2026-10-18): whether the help assistant may read whole pages (written into each account the person belongs to)
    security("account.sign_in_policy_changed", A), // C2, step-up
    security("verified_domain.added", A),
    security("verified_domain.verified", A),
    security("verified_domain.lapsed", A), // the recheck worker; enforcement stays (Q-D3)
    security("verified_domain.removed", A),
    security("allowed_network.added", A),
    security("allowed_network.removed", A),
    security("verified_domain.auto_join_changed", A), // C2 (added 2026-10-06): the auto-join role and named workspaces
    security("auth_session.stepped_up", AW), // C2 (added 2026-10-06): a step-up by password, authenticator or provider
    security("api_key.created", AW), // C3: account integration keys and workspace keys
    security("api_key.rotated", AW),
    security("api_key.revoked", AW),
    // plan-014 F3b (appended 2026-10-08): an operator sets a key's rate class (C3's customer API refuses `elevated`), and
    // an operator sends a pending domain's DNS record to the Owner again (never a verification: the customer verifies).
    security("api_key.rate_class_changed", AW),
    security("verified_domain.record_sent", A),
    // ── Financial: money, payer and documents (D1–D6, E1–E5) — the only rows Finance sees ────────────
    financial("account.terms_accepted", A), // B1 / E2 purchase terms (input 16)
    financial("account.trial_granted", A), // B1 (OD-15)
    financial("account.billing_timezone_scheduled", A), // takes effect at the next billing month
    financial("billing_profile.changed", A), // D5a; effective-dated, applies to the next document
    financial("billing_profile.tax_id_checked", A),
    financial("billing_assignment.changed", W), // D4: shared ⇄ dedicated funding (FR12-BIL-06)
    financial("billing_account.delegate_added", B), // DD14-15
    financial("billing_account.delegate_removed", B),
    financial("billing_account.suspended", B), // E5 / F3b
    financial("billing_account.suspension_lifted", B),
    financial("payment_method.added", B), // D3b
    financial("payment_method.default_changed", B),
    financial("payment_method.removed", B),
    financial("auto_reload.changed", B),
    financial("billing_operation.confirmed", AB), // D3a: a durable operation from a confirmed quote
    financial("billing_operation.cancelled", AB),
    financial("billing_operation.completed", AB),
    financial("billing_operation.failed", AB),
    financial("credit.transferred", B), // D4 (FR12-BIL-07)
    financial("account.credit_granted", B), // "Credit from WeTalk" (operator_credit), pre-plan-014 code
    financial("bank_allocation.recorded", B), // F3c: a manual bank payment allocated
    financial("refund.requested", B), // D3b (OD-13)
    financial("refund.approved", B), // F3c, an operator
    financial("refund.declined", B),
    financial("refund.waived", B), // B4: the owner waives a closure refund in writing
    financial("refund.line_paid_by_bank", B), // FIX-MONEY M4 (2026-10-06): a declined card refund line paid by bank transfer
    financial("subscription.started", B), // E2
    financial("subscription.changed", B),
    financial("subscription.cancellation_scheduled", B),
    financial("subscription.cancellation_withdrawn", B),
    financial("subscription.ended", B),
    financial("custom_offer.applied", A), // F3b, one operator alone with a reason (OD-11)
    financial("entitlement_allocation.changed", A), // E3a
    financial("spend_cap.changed", W), // E3a / ED-9: the workspace budget as the spend cap
    financial("account.concurrency_raised", A), // F3b, pre-plan-014 code
    financial("postpaid_contract.created", B), // E5
    financial("postpaid_contract.limit_changed", B),
    financial("postpaid_contract.ended", B),
    financial("financial_document.issued", AB), // D5b, the issuing worker
    financial("financial_document.resent", AB),
    financial("financial_document.credit_note_issued", AB),
    // plan-017 REG (appended 2026-10-11, design §29.9, §29.10; ADRs 0054–0055 proposed): registered ahead of their writers.
    // Design §29.9 calls them class `billing`; here that is the `financial` category (visible to Finance). The Audit log
    // heading follows the `account` prefix (`person`), like `account.credit_granted` (apps/api/.../event-class.ts).
    financial("account.extra_time_changed", A), // EXT: { old, new } — the organization's extra-time switch (PUT /v1/account/extra-time)
    financial("account.talk_rate_changed", A), // OPS-A: { talk_rate_change_id, starts_at, kinds: [{ kind, from, to }] } — an operator set own rates
    financial("account.talk_rate_cancelled", A), // OPS-A: { talk_rate_change_id, starts_at, kinds } — a scheduled own-rate change cancelled
    // plan-029 API-PRICE (appended 2026-10-29, design §40.25.1; ADR 0082 proposed): an operator set or cancelled the
    // organization's own confirmation code price (minor units and currency only).
    financial("account.confirmation_code_price_changed", A), // { action: set | cancel, effective_from, before, after }
    // plan-020 DOM (appended 2026-10-16, design §32.9.1, glossary §6.35; ADR 0070 proposed): registered ahead of their
    // writers (SUB-KIND, API-SUB, API-CARD, OPR-SUB). A plan is held by the shared balance, so each names the billing
    // account. The pre-plan-014 E2 rows above (`subscription.started`, `.changed`, `.cancellation_scheduled`,
    // `.cancellation_withdrawn`, `.ended`) stay as they are and are reused, never re-spelled.
    financial("subscription.purchased", B), // SUB-KIND: a plan bought (the consent id, the offer revision, the first charge)
    financial("subscription.card_replaced", B), // API-CARD: the card a running plan charges replaced (last four digits only)
    financial("subscription.cancelled_by_operator", B), // OPR-SUB: an operator ended the plan now (the cancel act's `now`)
    financial("subscription_period.waived", B), // OPR-SUB: an operator waived a due period (no charge, the period's grants)
    financial("subscription_period.credited", B), // OPR-SUB: an operator credited a paid period (credit note and card refund)
    // ── Operator: acts only WeTalk's operators take, shown to the customer (F3a/F3b/F3d) ─────────────
    operator("operator_approval.requested", AB), // F3a: above the threshold, waiting for a second operator
    operator("operator_approval.approved", AB),
    operator("operator_approval.declined", AB),
    operator("operator_approval.expired", AB),
    operator("person_security.authenticator_reset_started", A), // F3b/F3d: the person confirms by email
    operator("person_security.authenticator_reset", A),
    operator("person_security.signed_out_everywhere", A),
    operator("person_security.unlocked", A),
    operator("person_security.blocked", A),
    operator("account.created_by_operator", A), // F3d: the owner invitation is sent
    // plan-014 F3d (appended 2026-10-06): an operator-started email change the person confirmed from their current
    // address (the new address confirms it next, through C1's `person_security.email_changed`), and the person's own
    // confirmation of an operator action from the emailed link (the customer API; the person is the actor).
    operator("person_security.email_change_started", A),
    // The person's confirmation is THEIR act (a person actor), so it is filed as security, not as an operator's.
    security("operator_approval.customer_confirmed", A),
    // ── Sensitive reads: written before the read (audit-before-sensitive-read) ───────────────────────
    sensitiveRead("financial_document.downloaded", "financial", AB), // D5b: before the read-only SAS
    sensitiveRead("financial_document.exported", "financial", AB), // D6: the bounded CSV export
    sensitiveRead("ledger_entry.exported", "financial", WB),
    sensitiveRead("audit_entry.exported", "security", AW), // C4b, pre-plan-014 code
];
/** The catalogue, frozen, in the order above. */
export const AUDIT_ACTION_CATALOGUE = Object.freeze(CATALOGUE.map((entry) => Object.freeze({ ...entry, scope: Object.freeze([...entry.scope]) })));
const BY_CODE = new Map(AUDIT_ACTION_CATALOGUE.map((entry) => [entry.code, entry]));
export function isAuditActionCode(value) {
    return BY_CODE.has(value);
}
/** The catalogue row for a code, or `null` for a code the catalogue does not hold. */
export function auditCatalogueEntryOf(code) {
    return BY_CODE.get(code) ?? null;
}
// ── The safe-value contract ───────────────────────────────────────────────────────────────────────
/** A segment that refuses a key wherever it stands in it. */
export const REFUSED_AUDIT_KEY_SEGMENT = Object.freeze([
    "secret",
    "secrets",
    "token",
    "tokens",
    "password",
    "passwords",
    "passcode",
    "passphrase",
    "seed",
    "pepper",
    "pan",
    "iban",
    "cvv",
    "cvc",
    "otp",
    "totp",
    "pin",
    "transcript",
    "utterance",
]);
/** A whole key that is refused, though its segments may be harmless elsewhere (`country_code`). */
export const REFUSED_AUDIT_KEY = Object.freeze([
    "code",
    "recovery_code",
    "sign_in_code",
    "verification_code",
    "card_number",
    "private_key",
    "spoken_words",
    "party_address",
]);
/** A key ending in one of these names a value masked by construction, and is allowed. */
export const MASKED_AUDIT_KEY_SUFFIX = Object.freeze(["_last_four", "_masked"]);
/**
 * The largest before/after value the writer accepts, in bytes of its JSON text. A bound, not a figure:
 * an audit value is a handful of fields, and anything near this is content that does not belong in it.
 * 0081's CHECK holds a looser bound on the stored `jsonb` text, whose spacing differs.
 */
export const AUDIT_VALUE_MAX_BYTE = 8192;
/** The normalised spelling the key rule reads: snake_case, lower case. */
export function normalisedAuditKey(key) {
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
        .replace(/[-. ]/g, "_")
        .toLowerCase();
}
/** Whether a key may not appear anywhere in an audit value. The SQL twin is `audit_value_key_refused`. */
export function isRefusedAuditKey(key) {
    const normal = normalisedAuditKey(key);
    if (MASKED_AUDIT_KEY_SUFFIX.some((suffix) => normal.endsWith(suffix)))
        return false;
    if (REFUSED_AUDIT_KEY.includes(normal))
        return true;
    return normal.split("_").some((segment) => REFUSED_AUDIT_KEY_SEGMENT.includes(segment));
}
/** Why a value is refused, or `null` when it is safe. Never names the offending value, only its path. */
export function auditValueRefusal(value) {
    if (!isPlainRecord(value))
        return "an audit value is a record of named fields";
    const found = refusalAt(value, "$");
    if (found !== null)
        return found;
    const bytes = new TextEncoder().encode(JSON.stringify(value)).byteLength;
    if (bytes > AUDIT_VALUE_MAX_BYTE) {
        return `an audit value is at most ${String(AUDIT_VALUE_MAX_BYTE)} bytes of JSON (this one is ${String(bytes)})`;
    }
    return null;
}
/** Thrown by {@link auditValue} for a value the rule refuses. The message names a path, never a value. */
export class AuditValueRefusedError extends Error {
    name = "AuditValueRefusedError";
    constructor(reason) {
        super(`Refused audit value: ${reason}.`);
    }
}
/**
 * The only way to build an event's before/after value. A secret-shaped key does not compile; a value
 * that slips past the types (`as`, `any`, data from outside) is refused here at run time.
 */
export function auditValue(value) {
    const refusal = auditValueRefusal(value);
    if (refusal !== null)
        throw new AuditValueRefusedError(refusal);
    return Object.freeze(structuredClone(value));
}
function isPlainRecord(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function refusalAt(value, path) {
    if (value === null || typeof value === "string" || typeof value === "boolean")
        return null;
    if (typeof value === "number")
        return Number.isFinite(value) ? null : `${path} is not a finite number`;
    if (Array.isArray(value)) {
        for (const [index, element] of value.entries()) {
            const found = refusalAt(element, `${path}[${String(index)}]`);
            if (found !== null)
                return found;
        }
        return null;
    }
    if (isPlainRecord(value)) {
        for (const [key, element] of Object.entries(value)) {
            if (isRefusedAuditKey(key))
                return `${path} has the secret- or content-shaped key "${key}"`;
            const found = refusalAt(element, `${path}.${key}`);
            if (found !== null)
                return found;
        }
        return null;
    }
    return `${path} is a ${typeof value === "object" ? "non-JSON object" : typeof value}; say it as a string`;
}
//# sourceMappingURL=audit-event.js.map